import { useEffect, useMemo, useState } from 'react';
import type { MaBrandRow } from '../../services/microbrandAtlas.types';
import type { WatchBrand } from '../../types';
import type { CreateBrandSeed, EditBrandPatchSeed } from '../CuratedWatches/brandSeeds';
import { Badge } from '../common/Badge';
import { BrandLogo } from '../common/BrandLogo';
import { Button } from '../common/Button';
import { ComparisonGrid } from './ComparisonGrid';
import { ProposalCacheBar } from './ProposalCacheBar';
import {
  COUNTRY_WARNING,
  MA_SOURCE_LABEL,
  buildGridRows,
  buildSeedParts,
  defaultTicks,
  isTickable,
  mergeMaSource,
  toCreateSeed,
  type MaSource,
} from './fieldSpec';
import { normaliseBrandName } from './matching';
import { describeMatch, type ProposalMatch } from './proposalMatch';
import { getReviewDraft, reviewDraftKey, setReviewDraft } from './sessionCache';
import {
  SessionStatusBadge,
  clearSessionStatus,
  rejectAutoMatch,
  restoreAutoMatch,
  setSessionStatus,
  useSessionStatuses,
} from './sessionStatus';
import { retryMaBrandDetail, useMaBrandDetail } from './useMaBrandDetail';
import { refreshWkBrand } from './useMaBrands';

interface ProposalDetailProps {
  row: MaBrandRow;
  match: ProposalMatch;
  /** Every WK brand, for the manual "match to…" picker. */
  wkBrands: WatchBrand[];
  onBack: () => void;
  onCreate: (seed: CreateBrandSeed) => void;
  onUpdate: (seed: EditBrandPatchSeed) => void;
}

const PICKER_LIMIT = 8;

/**
 * One MA brand: who it is on the left, the WK pairing and the field-by-field
 * comparison on the right. Create and Update do not write anything themselves —
 * they hand a seed to BrandEditor, which opens pre-filled and does the save.
 */
export function ProposalDetail({ row, match, wkBrands, onBack, onCreate, onUpdate }: ProposalDetailProps) {
  const entry = useMaBrandDetail(row.slug);
  const { statuses, rejectedMatches } = useSessionStatuses();
  const status = statuses.get(row.slug);
  const ma = useMemo(() => mergeMaSource(row, entry.detail), [row, entry.detail]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const pickerResults = useMemo(() => {
    const needle = normaliseBrandName(pickerSearch);
    if (!needle) return [];
    return wkBrands
      .filter((brand) =>
        [brand.name, brand.id, ...(brand.alternativeNames ?? []), ...(brand.abbreviations ?? [])]
          .map(normaliseBrandName)
          .some((candidate) => candidate.includes(needle)),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, PICKER_LIMIT);
  }, [wkBrands, pickerSearch]);

  const mapTo = (brand: WatchBrand) => {
    setSessionStatus(row.slug, { kind: 'mapped', wkBrandId: brand.id });
    setPickerOpen(false);
    setPickerSearch('');
  };

  const unmatch = () => {
    // A manual mapping is dropped; an auto-match is rejected for the session.
    if (status?.kind === 'mapped') clearSessionStatus(row.slug);
    rejectAutoMatch(row.slug);
  };

  const wk = match.wkBrand;
  const dismissed = status?.kind === 'dismissed';

  // The WK brands were read once, when the tab first opened. Re-read the
  // matched one whenever this view opens on it, so an edit made elsewhere since
  // — ticking "Country verified" in the brand editor, say — is what the grid
  // compares against. One document, not the collection; the list keeps its
  // session-wide copy.
  //
  // The grid waits for the read to settle, so its ticks are worked out from the
  // fresh brand rather than from the stale one and then left standing. A failed
  // read settles too (the error is logged) and the grid falls back to the
  // cached copy.
  const wkId = wk?.id ?? null;
  const [refreshedWkId, setRefreshedWkId] = useState<string | null>(null);
  useEffect(() => {
    if (!wkId) return;
    let cancelled = false;
    void refreshWkBrand(wkId).then(() => {
      if (!cancelled) setRefreshedWkId(wkId);
    });
    return () => {
      cancelled = true;
    };
  }, [wkId]);
  const wkSettled = wkId === null || refreshedWkId === wkId;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onBack}>
          ← Back to list
        </Button>
        <h2 className="text-xl font-semibold text-gray-900">{row.name}</h2>
        <SessionStatusBadge status={status} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
        <aside className="space-y-3">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <BrandLogo src={ma.logo_url} name={row.name} />
              <div className="min-w-0">
                <div className="truncate font-semibold text-gray-900">{row.name}</div>
                <div className="truncate text-sm text-gray-500">{row.slug}</div>
              </div>
            </div>
            {ma.hero_image_url && (
              <img
                src={ma.hero_image_url}
                alt={`${row.name} hero`}
                loading="lazy"
                referrerPolicy="no-referrer"
                className="mt-3 max-h-48 w-full rounded border border-gray-200 object-cover"
              />
            )}
            <dl className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Country</dt>
                <dd className="text-right">{ma.country || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">City</dt>
                <dd className="text-right">
                  {[ma.city, ma.state].filter(Boolean).join(', ') || '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Founded</dt>
                <dd className="text-right">{ma.founded_year ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Lat, lng</dt>
                <dd className="text-right">
                  {typeof ma.latitude === 'number' && typeof ma.longitude === 'number' ? (
                    <a
                      href={`https://www.google.com/maps?q=${ma.latitude},${ma.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-700 underline"
                    >
                      {ma.latitude}, {ma.longitude}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
            </dl>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <span className="font-medium">⚠ Country needs checking.</span> {COUNTRY_WARNING}
          </div>
        </aside>

        <section className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-gray-800">WK match</h3>
            {wk ? (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900">{wk.name}</div>
                  <div className="text-sm text-gray-500">
                    {wk.id} · {describeMatch(match)}
                  </div>
                </div>
                {match.needsConfirm && <Badge variant="amber">Unconfirmed</Badge>}
                <div className="ml-auto flex flex-wrap gap-2">
                  {match.needsConfirm && (
                    <Button size="sm" onClick={() => mapTo(wk)}>
                      Confirm match
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setPickerOpen((open) => !open)}>
                    Change match
                  </Button>
                  <Button variant="ghost" size="sm" onClick={unmatch}>
                    Not a match
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-sm text-gray-600">
                  No WK brand is paired with this one — it will be created as a new brand.
                </p>
                <div className="ml-auto flex flex-wrap gap-2">
                  {rejectedMatches.has(row.slug) && match.auto && (
                    <Button variant="ghost" size="sm" onClick={() => restoreAutoMatch(row.slug)}>
                      Restore “{match.auto.wkBrand.name}”
                    </Button>
                  )}
                  <Button variant="secondary" size="sm" onClick={() => setPickerOpen((open) => !open)}>
                    Match to a WK brand…
                  </Button>
                </div>
              </div>
            )}
            {match.needsConfirm && (
              <p className="mt-2 text-sm text-amber-800">
                This pairing is only a partial name match. Confirm it before updating, or mark it as
                not a match to create a new brand instead.
              </p>
            )}
            {pickerOpen && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <label htmlFor="wk-brand-picker" className="mb-1 block text-sm font-medium text-gray-700">
                  Search WK brands
                </label>
                <input
                  id="wk-brand-picker"
                  type="search"
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Name, id or alias…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
                />
                {pickerSearch.trim() && (
                  <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {pickerResults.length === 0 && (
                      <li className="px-3 py-2 text-sm text-gray-500">No WK brand matches.</li>
                    )}
                    {pickerResults.map((brand) => (
                      <li key={brand.id}>
                        <button
                          type="button"
                          onClick={() => mapTo(brand)}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        >
                          <span className="font-medium text-gray-900">{brand.name}</span>
                          <span className="text-gray-500">{brand.id}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {(entry.status === 'idle' || entry.status === 'loading') && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
              Loading {row.name} from {MA_SOURCE_LABEL}…
            </div>
          )}

          {entry.status === 'ready' && !wkSettled && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
              Checking {wk?.name} for changes…
            </div>
          )}

          {entry.status === 'error' && (
            <div
              role="alert"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">Could not load this brand from {MA_SOURCE_LABEL}.</div>
                <div className="break-words">{entry.error}</div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => retryMaBrandDetail(row.slug)}>
                Retry
              </Button>
            </div>
          )}

          {entry.status === 'ready' && entry.detail && (
            <ProposalCacheBar slug={row.slug} entry={entry} ma={ma} />
          )}

          {entry.status === 'ready' && wkSettled && (
            <DetailReview
              // Ticks and edits start over whenever what is being compared
              // changes: another brand, or a different WK pairing.
              key={`${row.slug}|${wk?.id ?? ''}`}
              ma={ma}
              wk={wk}
              detailMissing={entry.detail === null}
              needsConfirm={match.needsConfirm}
              dismissed={dismissed}
              onCreate={onCreate}
              onUpdate={onUpdate}
              onDismiss={() => setSessionStatus(row.slug, { kind: 'dismissed' })}
              onUndismiss={() => clearSessionStatus(row.slug)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function DetailReview({
  ma,
  wk,
  detailMissing,
  needsConfirm,
  dismissed,
  onCreate,
  onUpdate,
  onDismiss,
  onUndismiss,
}: {
  ma: MaSource;
  wk: WatchBrand | null;
  detailMissing: boolean;
  needsConfirm: boolean;
  dismissed: boolean;
  onCreate: (seed: CreateBrandSeed) => void;
  onUpdate: (seed: EditBrandPatchSeed) => void;
  onDismiss: () => void;
  onUndismiss: () => void;
}) {
  const rows = useMemo(() => buildGridRows(ma, wk), [ma, wk]);
  const draftKey = reviewDraftKey(ma.slug, wk?.id);
  // Start from what the reviewer left here earlier this session, if anything.
  // Only ticks for rows that still exist are taken, laid over the defaults, so
  // a draft can never tick a row that has since become display-only or gone.
  const [ticks, setTicks] = useState(() => {
    const defaults = defaultTicks(rows);
    const saved = getReviewDraft(draftKey)?.ticks ?? {};
    return Object.fromEntries(
      Object.entries(defaults).map(([key, value]) => [key, key in saved ? saved[key] : value]),
    );
  });
  const [edits, setEdits] = useState<Record<string, string>>(
    () => getReviewDraft(draftKey)?.edits ?? {},
  );
  // Written through on every change, so the state outlives this component:
  // Create/Update unmount the whole tab while BrandEditor is open.
  useEffect(() => {
    setReviewDraft(draftKey, { ticks, edits });
  }, [draftKey, ticks, edits]);

  const tickedCount = rows.filter((row) => isTickable(row) && ticks[row.key]).length;
  // A locked conflict is not the reviewer's to resolve here, so it isn't counted.
  const conflictCount = rows.filter((row) => row.state === 'differ' && !row.locked).length;
  // BrandEditor derives the new brand's id from its name, so a Create with the
  // name unticked would open a form that cannot be saved.
  const createBlocked = !wk && !ticks.name;

  const seedParts = () => buildSeedParts({ ma, rows, ticks, edits });

  return (
    <>
      {detailMissing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {MA_SOURCE_LABEL} returned no detail row for this brand, so only the list columns are
          compared below.
        </div>
      )}

      {conflictCount > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {conflictCount} field{conflictCount === 1 ? '' : 's'} already hold a different value on WK
          and {conflictCount === 1 ? 'is' : 'are'} left unticked. Tick a field only to overwrite it.
        </div>
      )}

      <ComparisonGrid
        rows={rows}
        ticks={ticks}
        onToggle={(key, checked) => setTicks((prev) => ({ ...prev, [key]: checked }))}
        edits={edits}
        onEdit={(key, value) => setEdits((prev) => ({ ...prev, [key]: value }))}
        isCreate={!wk}
        rawPayload={ma}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="text-sm text-gray-600">
          {tickedCount} field{tickedCount === 1 ? '' : 's'} included. Nothing is written until you
          save in the brand editor.
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {dismissed ? (
            <Button variant="ghost" onClick={onUndismiss}>
              Undo dismiss
            </Button>
          ) : (
            <Button variant="ghost" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {wk ? (
            <Button
              disabled={needsConfirm}
              title={needsConfirm ? 'Confirm the match first' : undefined}
              onClick={() =>
                onUpdate({ ...seedParts(), sourceLabel: MA_SOURCE_LABEL, brandId: wk.id })
              }
            >
              Update matched WK brand…
            </Button>
          ) : (
            <Button
              disabled={createBlocked}
              title={createBlocked ? 'Name must be included to create a brand' : undefined}
              onClick={() => onCreate(toCreateSeed(seedParts()))}
            >
              Create new WK brand…
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
